import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, Search, MessageSquare, FileCheck, Lightbulb } from 'lucide-react';

export default function ResumeReviewPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.resumeReview} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Expert Feedback</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Review & Audit Service
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Get expert feedback on your current resume. Understand what's working, what needs improvement, and what to prioritize for better results.
            </p>
            <Button 
              onClick={() => navigate('/audit')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Request Free Quick Audit <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* What You Get */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            What You Get With a Resume Review
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Search, title: 'ATS Compatibility Check', desc: 'We review your resume\'s ability to pass through Applicant Tracking Systems.' },
              { icon: FileCheck, title: 'Formatting Assessment', desc: 'Feedback on layout, structure, and visual presentation of your resume.' },
              { icon: MessageSquare, title: 'Content Feedback', desc: 'Analysis of your bullet points, achievements, and overall messaging.' },
              { icon: Lightbulb, title: 'Improvement Priorities', desc: 'Clear guidance on what to fix first for the biggest impact.' },
              { icon: Check, title: 'Honest Assessment', desc: 'Straightforward feedback—no sugar-coating, just useful insights.' },
              { icon: Check, title: 'Next Steps', desc: 'Recommendations on whether a refresh or full rewrite makes sense.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Free vs Detailed */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Free Quick Audit vs. Detailed Review
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6 rounded-2xl border-2 border-teal-500 bg-teal-50/30">
                <Badge className="bg-teal-600 text-white mb-4">Free</Badge>
                <h3 className="font-bold text-slate-900 mb-4 text-lg">Quick Audit</h3>
                <p className="text-slate-600 mb-4">A brief, no-obligation look at your resume with initial feedback.</p>
                <ul className="space-y-2">
                  {['High-level observations', 'Major issues identified', 'General recommendations', 'No payment required'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-teal-600" /> {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/audit')}
                  variant="outline"
                  className="w-full mt-4 rounded-full"
                >
                  Request Free Audit
                </Button>
              </Card>
              
              <Card className="p-6 rounded-2xl border-2 border-slate-200">
                <Badge className="bg-slate-600 text-white mb-4">Paid Service</Badge>
                <h3 className="font-bold text-slate-900 mb-4 text-lg">Detailed Review</h3>
                <p className="text-slate-600 mb-4">Comprehensive analysis with specific, actionable feedback.</p>
                <ul className="space-y-2">
                  {['Section-by-section review', 'Specific rewrite suggestions', 'ATS keyword analysis', 'Priority action list'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-slate-600" /> {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate('/start')}
                  className="w-full mt-4 rounded-full bg-slate-900"
                >
                  Submit Intake for Review
                </Button>
              </Card>
            </div>
          </div>
        </section>

        {/* After the Review */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            What Happens After the Review?
          </h2>
          
          <div className="bg-slate-50 p-6 rounded-2xl space-y-4">
            <p className="text-slate-700">
              After we review your resume, we'll share our findings and recommendations. You'll understand:
            </p>
            <ul className="space-y-2">
              {[
                'Whether your resume needs minor updates or a full rewrite',
                'What specific improvements would have the most impact',
                'How your resume currently performs for ATS systems',
                'Whether additional services like LinkedIn or cover letter would help',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-700">
                  <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-slate-600 mt-4">
              If you decide to proceed with resume work, we'll confirm the scope and send your invoice. No pressure—the review helps you make an informed decision.
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite</Link>
              <span className="text-slate-300">|</span>
              <Link to="/services/resume-refresh" className="text-teal-700 hover:text-teal-800 underline">Resume Refresh</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View All Pricing</Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Want feedback on your resume?
            </h3>
            <p className="text-slate-300 mb-6">
              Start with a free quick audit to get initial feedback, or submit your intake for a detailed review.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/audit')}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-8"
              >
                Free Quick Audit
              </Button>
              <Button 
                onClick={() => navigate('/start')}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
              >
                Start Full Intake
              </Button>
            </div>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
